import { useTranslation } from "react-i18next"
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts"

import { type ChartConfig, ChartContainer } from "@/components/ui/chart"
import type { TimeseriesPoint } from "@/types/creator-analytics"

export interface CreatorAnalyticsLineChartProps {
  /** Stable id for the chart (used as the test-id slug). */
  chartId: string
  /** Data points — gap days are encoded as `value: null` (TRD K11). */
  data: TimeseriesPoint[]
  /** CSS variable name (e.g., `--chart-1`) for the series color. */
  colorVar: string
  /** ARIA label summarising the chart. Composed by the parent. */
  ariaLabel: string
  /** Tooltip unit string (e.g., "followers"). */
  unit: string
  /** ARIA-only mirror table heading text. */
  mirrorTableHeading: string
}

/**
 * OKS-64 — Single-series line chart for follower-growth and
 * engagement-trend. Recharts `LineChart` with `connectNulls={false}`
 * so a gap day renders as a literal break (TRD K11 / K21 — PRD §3
 * gap-preservation lock).
 *
 * SR-only mirror `<table>` follows the OKS-21 invite-activity-chart
 * convention: one row per data point so screen-reader users can
 * navigate the dataset cell-by-cell (UX Spec §6 a11y).
 */
export function CreatorAnalyticsLineChart({
  chartId,
  data,
  colorVar,
  ariaLabel,
  unit,
  mirrorTableHeading,
}: CreatorAnalyticsLineChartProps) {
  const { t } = useTranslation()

  // Build the ChartContainer config — single series, color from CSS var.
  const chartConfig: ChartConfig = {
    value: {
      label: unit,
      color: `var(${colorVar})`,
    },
  }

  return (
    <div
      role="img"
      aria-label={ariaLabel}
      data-testid={`creator-analytics-timeseries-${chartId}-chart`}
    >
      <ChartContainer config={chartConfig} className="h-48 w-full sm:h-56">
        <ResponsiveContainer>
          <LineChart
            data={data}
            margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={20} />
            <YAxis
              tick={{ fontSize: 11 }}
              tickFormatter={formatCompact}
              width={48}
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke={`var(${colorVar})`}
              strokeWidth={2}
              dot={false}
              connectNulls={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </ChartContainer>

      <table className="sr-only">
        <caption>{mirrorTableHeading}</caption>
        <thead>
          <tr>
            <th scope="col">{t("crew.analytics.timeseries.gap_tooltip")}</th>
            <th scope="col">{unit}</th>
          </tr>
        </thead>
        <tbody>
          {data.map((point) => (
            <tr key={point.date}>
              <td>{point.date}</td>
              <td>{point.value ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function formatCompact(value: number): string {
  return new Intl.NumberFormat(undefined, {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value)
}

export default CreatorAnalyticsLineChart
